/**
 * บัญชีฝากเงิน (public) — สำหรับหน้าฝากเงิน user-modern
 * เรียก API เดียวกับที่ frontend-admin จัดการ (configuration-service)
 * GET /api/v1/configuration-service/deposit-accounts/:tenantId/public
 */
import { resolveConfiguredBaseUrl, withApiBaseUrl } from '../lib/apiBaseUrl'

const getConfigurationServiceBaseUrl = (): string => {
  const configuredServiceBaseUrl = resolveConfiguredBaseUrl(
    import.meta.env.VITE_CONFIGURATION_SERVICE_URL as string | undefined
  )

  if (configuredServiceBaseUrl !== null) {
    return configuredServiceBaseUrl || withApiBaseUrl('/api/v1/configuration-service')
  }

  return withApiBaseUrl('/api/v1/configuration-service')
}

const withConfigurationServiceBaseUrl = (path: string): string => {
  const normalizedPath = path.startsWith('/') ? path : `/${path}`
  return `${getConfigurationServiceBaseUrl()}${normalizedPath}`
}

export interface PublicDepositAccount {
  _id: string
  accountName: string
  bankName: string
  bankCode: string
  bankIcon?: string
  accountType: 'savings' | 'current' | 'business'
  isActive: boolean
  isDefault: boolean
  description?: string
  minDepositAmount?: number | null
  maxDepositAmount?: number | null
  currency: string
  accountNumber: string
  depositSourceMode?: DepositAccountSourceMode
}

export type DepositAccountSourceMode = 'both' | 'slip_only' | 'sms_only'

export interface PublicPromptPayAccount {
  _id: string
  accountName: string
  promptPayId: string
  promptPayType: 'phone_number' | 'national_id' | 'tax_id' | 'ewallet' | 'custom'
  description?: string
  minDepositAmount?: number | null
  maxDepositAmount?: number | null
  isActive: boolean
}

interface DepositAccountListResponse {
  success: boolean
  data: PublicDepositAccount[]
  message?: string
  error?: string
}

interface PromptPayAccountResponse {
  success: boolean
  data?: PublicPromptPayAccount[] | null
  message?: string
  error?: string
}

export const isTrueMoneyDepositAccount = (account: Pick<PublicDepositAccount, 'bankCode' | 'bankName'>): boolean => {
  const code = String(account.bankCode || '').toUpperCase()
  const name = String(account.bankName || '').toLowerCase()
  return code === 'TRUE' || name.includes('true money') || name.includes('truemoney')
}

export const isPromptPayDepositAccount = (account: Pick<PublicDepositAccount, 'bankCode' | 'bankName'>): boolean => {
  const code = String(account.bankCode || '').toUpperCase()
  const name = String(account.bankName || '').toLowerCase()
  return code === 'PROMPTPAY' || name.includes('promptpay') || name.includes('prompt pay')
}

export const getDepositAccountSourceMode = (
  account?: Pick<PublicDepositAccount, 'depositSourceMode'> | null
): DepositAccountSourceMode => {
  const rawMode = String(account?.depositSourceMode || '').trim().toLowerCase()
  if (rawMode === 'slip_only' || rawMode === 'sms_only') {
    return rawMode
  }
  return 'both'
}

export const isSmsOnlyDepositAccount = (
  account?: Pick<PublicDepositAccount, 'depositSourceMode'> | null
): boolean => getDepositAccountSourceMode(account) === 'sms_only'

export const isSlipOnlyDepositAccount = (
  account?: Pick<PublicDepositAccount, 'depositSourceMode'> | null
): boolean => getDepositAccountSourceMode(account) === 'slip_only'

const hasConfiguredAmountRange = (account: PublicDepositAccount): boolean =>
  typeof account.minDepositAmount === 'number' && typeof account.maxDepositAmount === 'number'

export const resolveDepositAccountForAmount = (
  accounts: PublicDepositAccount[],
  amount: number,
  options?: { channel?: 'bank' | 'true_money' | 'promptpay' }
): PublicDepositAccount | null => {
  const channel = options?.channel ?? 'bank'
  const candidates = accounts.filter((account) => {
    if (channel === 'true_money') return isTrueMoneyDepositAccount(account)
    if (channel === 'promptpay') return isPromptPayDepositAccount(account)
    return !isTrueMoneyDepositAccount(account) && !isPromptPayDepositAccount(account)
  })

  if (!Number.isFinite(amount) || amount <= 0) {
    return candidates.find((account) => account.isDefault) || candidates[0] || null
  }

  const rangedAccounts = candidates.filter(hasConfiguredAmountRange)
  if (rangedAccounts.length === 0) {
    return candidates.find((account) => account.isDefault) || candidates[0] || null
  }

  return rangedAccounts.find((account) =>
    amount >= Number(account.minDepositAmount) && amount <= Number(account.maxDepositAmount)
  ) || null
}

const DEPOSIT_ACCOUNT_CLIENT_TIMEOUT_MS = 8000
const DEPOSIT_ACCOUNT_CACHE_TTL_MS = 5 * 60 * 1000
const depositAccountsCache = new Map<string, { value: PublicDepositAccount[]; expiresAt: number }>()
const promptPayAccountsCache = new Map<string, { value: PublicPromptPayAccount[]; expiresAt: number }>()

async function fetchWithClientTimeout(input: string, init: RequestInit, timeoutMs: number): Promise<Response> {
  const controller = new AbortController()
  const timer = setTimeout(() => controller.abort(), timeoutMs)
  try {
    return await fetch(input, { ...init, signal: controller.signal })
  } finally {
    clearTimeout(timer)
  }
}

export async function getPublicDepositAccounts(
  tenantId: string,
  options?: { forceRefresh?: boolean }
): Promise<PublicDepositAccount[]> {
  const cacheKey = String(tenantId || '')
  if (!options?.forceRefresh) {
    const cached = depositAccountsCache.get(cacheKey)
    if (cached && cached.expiresAt > Date.now()) {
      return cached.value
    }
  }

  try {
    const res = await fetchWithClientTimeout(
      withConfigurationServiceBaseUrl(`/deposit-accounts/${encodeURIComponent(tenantId)}/public`),
      {
        method: 'GET',
        headers: { 'Content-Type': 'application/json' },
      },
      DEPOSIT_ACCOUNT_CLIENT_TIMEOUT_MS
    )
    if (!res.ok) {
      return depositAccountsCache.get(cacheKey)?.value || []
    }
    const data: DepositAccountListResponse = await res.json()
    const accounts = data.success && Array.isArray(data.data) ? data.data : []
    depositAccountsCache.set(cacheKey, {
      value: accounts,
      expiresAt: Date.now() + DEPOSIT_ACCOUNT_CACHE_TTL_MS,
    })
    return accounts
  } catch (e) {
    if ((e as Error)?.name === 'AbortError') {
      console.warn('Deposit accounts request timed out, using fallback')
    } else {
      console.error('Error fetching public deposit accounts:', e)
    }
    return depositAccountsCache.get(cacheKey)?.value || []
  }
}

export async function getDefaultDepositAccount(
  tenantId: string
): Promise<PublicDepositAccount | null> {
  const accounts = await getPublicDepositAccounts(tenantId)
  return accounts.find((a) => a.isDefault) || accounts[0] || null
}

export const mapPromptPayAccountToDepositAccount = (
  account: PublicPromptPayAccount
): PublicDepositAccount => ({
  _id: `promptpay:${account._id}`,
  accountName: account.accountName,
  bankName: 'PromptPay',
  bankCode: 'PROMPTPAY',
  bankIcon: '',
  accountType: 'savings',
  isActive: account.isActive,
  isDefault: false,
  description: account.description || '',
  minDepositAmount: account.minDepositAmount ?? null,
  maxDepositAmount: account.maxDepositAmount ?? null,
  currency: 'THB',
  accountNumber: account.promptPayId,
})

export const mapPromptPayAccountsToDepositAccounts = (
  accounts: PublicPromptPayAccount[]
): PublicDepositAccount[] => accounts.map(mapPromptPayAccountToDepositAccount)

export async function getPublicPromptPayAccounts(
  tenantId: string,
  options?: { forceRefresh?: boolean }
): Promise<PublicPromptPayAccount[]> {
  const cacheKey = String(tenantId || '')
  if (!options?.forceRefresh) {
    const cached = promptPayAccountsCache.get(cacheKey)
    if (cached && cached.expiresAt > Date.now()) {
      return cached.value
    }
  }

  try {
    const res = await fetchWithClientTimeout(
      withConfigurationServiceBaseUrl(`/promptpay-accounts/${encodeURIComponent(tenantId)}/public`),
      {
        method: 'GET',
        headers: { 'Content-Type': 'application/json' },
      },
      DEPOSIT_ACCOUNT_CLIENT_TIMEOUT_MS
    )
    if (!res.ok) {
      return promptPayAccountsCache.get(cacheKey)?.value || []
    }
    const data: PromptPayAccountResponse = await res.json()
    const accounts = data.success && Array.isArray(data.data) ? data.data : []
    promptPayAccountsCache.set(cacheKey, {
      value: accounts,
      expiresAt: Date.now() + DEPOSIT_ACCOUNT_CACHE_TTL_MS,
    })
    return accounts
  } catch (e) {
    if ((e as Error)?.name === 'AbortError') {
      console.warn('PromptPay accounts request timed out, using fallback')
    } else {
      console.error('Error fetching public promptpay accounts:', e)
    }
    return promptPayAccountsCache.get(cacheKey)?.value || []
  }
}

const DEFAULT_DECIMAL_COUNTDOWN_MINUTES = 5
const DEFAULT_PRESET_AMOUNTS = [100, 200, 300, 500, 1000, 2000, 3000, 5000, 10000]
const MAX_PRESET_AMOUNTS = 12

export interface DepositChannels {
  bank: boolean
  trueMoney: boolean
  promptPay: boolean
  decimalReservation: boolean
  allowSubmitWithoutSlip: boolean
}

const DEFAULT_DEPOSIT_CHANNELS: DepositChannels = {
  bank: true,
  trueMoney: true,
  promptPay: true,
  decimalReservation: true,
  allowSubmitWithoutSlip: true,
}

const buildWithdrawalSettingsUrl = (tenantId: string): string =>
  `${withConfigurationServiceBaseUrl('/withdrawal/settings')}?tenantScope=${encodeURIComponent(tenantId)}&_t=${Date.now()}`

const getNoCacheRequestInit = (): RequestInit => ({
  method: 'GET',
  headers: {
    'Content-Type': 'application/json',
    'Cache-Control': 'no-cache, no-store, must-revalidate',
    Pragma: 'no-cache',
    Expires: '0',
  },
  cache: 'no-store',
})

function normalizePresetAmounts(values: unknown): number[] {
  if (!Array.isArray(values)) return DEFAULT_PRESET_AMOUNTS
  const normalized = values
    .map((value) => Number(value))
    .filter((value, index, arr) => Number.isFinite(value) && value > 0 && arr.indexOf(value) === index)
    .slice(0, MAX_PRESET_AMOUNTS)
  return normalized.length > 0 ? normalized : DEFAULT_PRESET_AMOUNTS
}

interface WithdrawalSettingsRaw {
  depositChannels?: Partial<Record<keyof DepositChannels, boolean>> & {
    decimalReservationCountdownMinutes?: number
    quickDepositEnabled?: boolean
    presetAmounts?: number[]
  }
}

const WITHDRAWAL_SETTINGS_CACHE_TTL_MS = 3 * 60 * 1000
const withdrawalSettingsCache = new Map<string, { value: WithdrawalSettingsRaw; expiresAt: number }>()
const withdrawalSettingsInflight = new Map<string, Promise<WithdrawalSettingsRaw>>()

async function loadWithdrawalSettings(
  tenantId: string,
  options?: { forceRefresh?: boolean }
): Promise<WithdrawalSettingsRaw> {
  const cacheKey = String(tenantId || '')
  if (!options?.forceRefresh) {
    const cached = withdrawalSettingsCache.get(cacheKey)
    if (cached && cached.expiresAt > Date.now()) {
      return cached.value
    }
    const inflight = withdrawalSettingsInflight.get(cacheKey)
    if (inflight) {
      return inflight
    }
  }

  const promise = (async (): Promise<WithdrawalSettingsRaw> => {
    try {
      const res = await fetchWithClientTimeout(
        buildWithdrawalSettingsUrl(tenantId),
        getNoCacheRequestInit(),
        DEPOSIT_ACCOUNT_CLIENT_TIMEOUT_MS
      )
      if (!res.ok) {
        return withdrawalSettingsCache.get(cacheKey)?.value || {}
      }
      const data = (await res.json()) as {
        success?: boolean
        data?: { settings?: WithdrawalSettingsRaw }
      }
      const settings = data?.data?.settings || {}
      withdrawalSettingsCache.set(cacheKey, {
        value: settings,
        expiresAt: Date.now() + WITHDRAWAL_SETTINGS_CACHE_TTL_MS,
      })
      return settings
    } catch (e) {
      if ((e as Error)?.name === 'AbortError') {
        console.warn('Withdrawal settings request timed out, using fallback')
      } else {
        console.error('Error fetching withdrawal settings:', e)
      }
      return withdrawalSettingsCache.get(cacheKey)?.value || {}
    } finally {
      withdrawalSettingsInflight.delete(cacheKey)
    }
  })()

  withdrawalSettingsInflight.set(cacheKey, promise)
  return promise
}

/** ช่องทางฝากจาก Admin (ตั้งค่าที่ settings/withdrawal) — ใช้ซ่อน/แสดงและปิดการกดในแต่ละแท็บฝาก */
export async function getDepositChannels(
  tenantId: string,
  options?: { forceRefresh?: boolean }
): Promise<DepositChannels> {
  const settings = await loadWithdrawalSettings(tenantId, options)
  const ch = settings?.depositChannels
  if (!ch || typeof ch !== 'object') return DEFAULT_DEPOSIT_CHANNELS
  return {
    bank: ch.bank !== false,
    trueMoney: ch.trueMoney !== false,
    promptPay: ch.promptPay !== false,
    decimalReservation: ch.decimalReservation !== false,
    allowSubmitWithoutSlip: ch.allowSubmitWithoutSlip !== false,
  }
}

/** เวลา countdown หน้ารอโอนเงินจองทศนิยม (นาที) จาก configuration-service — แต่ละ tenant ตั้งได้ต่างกัน */
export async function getDecimalReservationCountdownMinutes(
  tenantId: string,
  options?: { forceRefresh?: boolean }
): Promise<number> {
  const settings = await loadWithdrawalSettings(tenantId, options)
  const minutes = settings?.depositChannels?.decimalReservationCountdownMinutes
  if (typeof minutes === 'number' && minutes >= 1 && minutes <= 60) return minutes
  return DEFAULT_DECIMAL_COUNTDOWN_MINUTES
}

export async function getDepositPresetAmounts(
  tenantId: string,
  options?: { forceRefresh?: boolean }
): Promise<number[]> {
  const settings = await loadWithdrawalSettings(tenantId, options)
  if (settings?.depositChannels?.quickDepositEnabled !== true) {
    return []
  }
  return normalizePresetAmounts(settings?.depositChannels?.presetAmounts)
}

export type DepositChannelsWsPayload = Partial<DepositChannels> & {
  quickDepositEnabled?: boolean
  presetAmounts?: number[]
  presetAmountCount?: number
}

/** ล้าง cache withdrawal settings หลังได้รับ WebSocket จาก admin */
export function invalidateWithdrawalSettingsCache(tenantId: string): void {
  const cacheKey = String(tenantId || '')
  withdrawalSettingsCache.delete(cacheKey)
  withdrawalSettingsInflight.delete(cacheKey)
}

/** อ่านยอดปุ่มด่วนจาก payload WS ทันที (null = ต้อง fetch ใหม่) */
export function resolvePresetAmountsFromDepositChannels(
  channels?: DepositChannelsWsPayload | null
): number[] | null {
  if (!channels || typeof channels !== 'object') return null
  if (
    !('quickDepositEnabled' in channels)
    && !('presetAmounts' in channels)
    && !('presetAmountCount' in channels)
  ) {
    return null
  }
  if (channels.quickDepositEnabled !== true) return []
  if (Array.isArray(channels.presetAmounts)) {
    return normalizePresetAmounts(channels.presetAmounts)
  }
  return null
}

/** ซิงก์ปุ่มยอดด่วนหลัง deposit_channels_updated — แสดง/ซ่อนทันที */
export async function syncPresetAmountsAfterDepositChannelsUpdate(
  tenantId: string,
  channels?: DepositChannelsWsPayload | null
): Promise<number[]> {
  invalidateWithdrawalSettingsCache(tenantId)
  const instant = resolvePresetAmountsFromDepositChannels(channels)
  if (instant !== null) return instant
  return getDepositPresetAmounts(tenantId, { forceRefresh: true })
}
